import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardActions,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Photo as PhotoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { Package } from '../../../types/Venue';
import { uploadPackageImage } from '../../../services/venueService';

interface PackageImagesFormProps {
  packageData: Partial<Package>;
  onChange: (data: Partial<Package>) => void;
  venueId: string;
}

const PackageImagesForm: React.FC<PackageImagesFormProps> = ({
  packageData,
  onChange,
  venueId
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tempPackageId, setTempPackageId] = useState(packageData.id || `temp-${Date.now()}`);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      const imagePromises = Array.from(files).map(async (file) => {
        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`File ${file.name} is too large. Maximum size is 5MB.`);
        }

        // Check file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`File ${file.name} is not an image.`);
        }

        return await uploadPackageImage(venueId, tempPackageId, file);
      });

      const newImageUrls = await Promise.all(imagePromises);
      const currentImages = packageData.images || [];
      onChange({ images: [...currentImages, ...newImageUrls] });
    } catch (err) {
      console.error('Error uploading images:', err);
      setError(typeof err === 'string' ? err : (err as Error).message || 'Failed to upload images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const updatedImages = [...(packageData.images || [])];
    updatedImages.splice(index, 1);
    onChange({ images: updatedImages });
  };

  const handleGenerateAIImages = () => {
    // This would integrate with your existing Stability AI implementation
    alert('AI image generation will be implemented here');
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        Package Images
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload images that showcase your package. High-quality images will attract more customers.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 3,
              textAlign: 'center',
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: 'divider',
              backgroundColor: 'background.default'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={uploading}
            />
            <Typography variant="h6" gutterBottom>
              <UploadIcon sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} />
              <br />
              Drag & Drop or Select Images
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Accepted formats: JPG, PNG, WebP. Max size: 5MB per image.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              sx={{ mr: 1 }}
            >
              {uploading ? <CircularProgress size={24} /> : 'Select Files'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PhotoIcon />}
              onClick={handleGenerateAIImages}
              disabled={uploading}
            >
              Generate with AI
            </Button>
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Uploaded Images
          </Typography>
          
          {(packageData.images || []).length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: 'background.default' }}>
              <Typography variant="body2" color="text.secondary">
                No images uploaded yet. Add at least one image to showcase your package.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {(packageData.images || []).map((imageUrl, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Card sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={imageUrl}
                      alt={`Package image ${index + 1}`}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardActions sx={{ justifyContent: 'space-between', p: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Image {index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveImage(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Image Tips
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Use high-quality, well-lit images<br />
            • Show the actual experience customers will receive<br />
            • Include images of the venue, food, or activities included<br />
            • Maintain a consistent style across all images<br />
            • The first image will be used as the main package image
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PackageImagesForm;
